import React, { useEffect, useRef, useState } from 'react';
import { Plus, X, Upload, ChevronDown, ChevronUp, Film, Users, Image as ImageIcon, Video as VideoIcon, UserPlus, Check, Info, Volume2, VolumeX, Clock, Maximize2, Sparkles } from 'lucide-react';
import { apiClient } from '../../utils/apiClient';

// Sea-Dance 2 unterstützt bis zu 12 Reference-Bilder/Videos
const MAX_REFERENCES = 12;

// Slot-Verwendungs-Optionen — direkt was Sea-Dance kennt
const USE_AS_OPTIONS = [
  { value: 'start',     label: 'Start Frame' },
  { value: 'end',       label: 'End Frame' },
  { value: 'reference', label: 'Referenz' },
];

// ═══════════════════════════════════════════════════════════════
// REFERENZ-PICKER MODAL — 3 Karten: Avatare · Bilder · Videos
// ═══════════════════════════════════════════════════════════════
const ReferencePickerModal = ({ open, onClose, onPick, activeAvatarId }) => {
  const [tab, setTab] = useState('avatars');
  const [avatars, setAvatars] = useState([]);
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showInviteSoon, setShowInviteSoon] = useState(false);
  const fileInputImage = useRef(null);
  const fileInputVideo = useRef(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    (async () => {
      try {
        // Avatare
        const ja = await apiClient.get('/api/training/list');
        if (ja.ok) {
          const j = await ja.json();
          setAvatars((j.jobs || []).filter(a => a.status === 'ready'));
        }
        // Bilder + Videos vom aktiven Avatar
        // Prop hat Vorrang (folgt der oben gewählten Avatar-Auswahl), localStorage als Fallback.
        const effAvatarId = activeAvatarId
          || JSON.parse(localStorage.getItem('snapshot_current_avatar_id') || 'null');
        if (effAvatarId) {
          const jg = await apiClient.get(`/api/generations/list?avatar_id=${effAvatarId}`);
          if (jg.ok) {
            const j = await jg.json();
            setImages(j.generations || []);
          }
          const jv = await apiClient.get(`/api/videos/list?avatar_id=${effAvatarId}`);
          if (jv.ok) {
            const j = await jv.json();
            setVideos(j.videos || j.generations || []);
          }
        }
      } catch (e) {
        console.warn('Modal-Load fehlgeschlagen', e);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, [open, activeAvatarId]);

  const handleUpload = async (file, kind) => {
    // Vorerst: lokale Object-URL → User sieht das Bild sofort. Backend-Upload kommt später.
    const url = URL.createObjectURL(file);
    onPick({
      id: `local-${Date.now()}`,
      kind,
      url,
      thumbUrl: url,
      label: file.name,
      isLocal: true,
    });
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-black border-2 border-[#10B981]/30 rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 flex-shrink-0">
          <h3 className="text-white font-bold text-lg">Referenz auswählen</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab-Karten */}
        <div className="grid grid-cols-3 gap-2 p-3 flex-shrink-0">
          {[
            { id: 'avatars', label: 'Avatare', icon: Users },
            { id: 'images',  label: 'Bilder',  icon: ImageIcon },
            { id: 'videos',  label: 'Videos',  icon: VideoIcon },
          ].map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`px-3 py-3 rounded-xl font-semibold text-sm transition-all flex flex-col items-center gap-1 ${
                  active
                    ? 'bg-gradient-to-br from-[#10B981] to-[#059669] text-black shadow-lg'
                    : 'bg-white/5 text-white/70 border border-white/10 hover:bg-white/10'
                }`}>
                <Icon className="w-5 h-5" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Body — scrollbar */}
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {loading && (
            <div className="text-white/40 text-center py-10">Wird geladen…</div>
          )}

          {!loading && tab === 'avatars' && (
            <div className="space-y-4">
              <div>
                <h4 className="text-white/70 text-xs uppercase tracking-wider font-bold mb-2">Meine Avatare</h4>
                {avatars.length === 0 ? (
                  <p className="text-white/40 text-sm py-6 text-center">Noch keine Avatare vorhanden.</p>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {avatars.map(a => (
                      <button key={a.id}
                        onClick={() => { onPick({ id: a.id, kind: 'avatar', url: a.profile_image_url, thumbUrl: a.profile_image_url, label: a.title, avatarId: a.id }); onClose(); }}
                        className="aspect-[3/4] rounded-xl overflow-hidden border-2 border-white/10 hover:border-[#10B981] transition-all relative group">
                        {a.profile_image_url ? (
                          <img src={a.profile_image_url} alt={a.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-white/5 flex items-center justify-center text-white/30 text-xs">{a.title}</div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs font-semibold px-2 py-1 truncate">
                          {a.title}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {/* Freund einladen */}
              <div className="border-t border-white/10 pt-4">
                <button
                  onClick={() => { setShowInviteSoon(true); setTimeout(() => setShowInviteSoon(false), 2500); }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/5 border-2 border-dashed border-white/20 text-white/70 hover:bg-white/10 hover:border-[#10B981]/50 transition-all">
                  <UserPlus className="w-5 h-5" />
                  Freund einladen
                </button>
                {showInviteSoon && (
                  <p className="text-[#10B981] text-xs text-center mt-2 animate-pulse">Kommt in Kürze</p>
                )}
              </div>
            </div>
          )}

          {!loading && tab === 'images' && (
            <div className="space-y-4">
              {/* Eigenes hinzufügen */}
              <button
                onClick={() => fileInputImage.current?.click()}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/5 border-2 border-dashed border-white/20 text-white/80 hover:bg-white/10 hover:border-[#10B981]/50 transition-all">
                <Upload className="w-5 h-5" />
                Eigenes Bild hinzufügen
              </button>
              <input ref={fileInputImage} type="file" accept="image/*"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f, 'image'); e.target.value = ''; }}
                className="sr-only" />

              {/* Galerie */}
              {images.length === 0 ? (
                <p className="text-white/40 text-sm py-6 text-center">Keine Bilder in deiner Galerie.</p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {images.flatMap(g => (g.image_urls || g.thumbnail_urls || []).map((u, i) => ({
                    key: `${g.id}_${i}`,
                    url: u,
                    thumbUrl: (g.thumbnail_urls && g.thumbnail_urls[i]) || u,
                    label: g.prompt_text || 'Bild',
                  }))).map(item => (
                    <button key={item.key}
                      onClick={() => { onPick({ id: item.key, kind: 'image', url: item.url, thumbUrl: item.thumbUrl, label: item.label }); onClose(); }}
                      className="aspect-[3/4] rounded-lg overflow-hidden border-2 border-white/10 hover:border-[#10B981] transition-all">
                      <img src={item.thumbUrl} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {!loading && tab === 'videos' && (
            <div className="space-y-4">
              <button
                onClick={() => fileInputVideo.current?.click()}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/5 border-2 border-dashed border-white/20 text-white/80 hover:bg-white/10 hover:border-[#10B981]/50 transition-all">
                <Upload className="w-5 h-5" />
                Eigenes Video hinzufügen
              </button>
              <input ref={fileInputVideo} type="file" accept="video/*"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f, 'video'); e.target.value = ''; }}
                className="sr-only" />

              {videos.length === 0 ? (
                <p className="text-white/40 text-sm py-6 text-center">Keine Videos in deiner Galerie.</p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {videos.map(v => {
                    const url = v.video_url || v.preview_url;
                    const thumb = v.preview_url || v.thumbnail_url || v.source_image_url || url;
                    if (!url) return null;
                    return (
                      <button key={v.id}
                        onClick={() => { onPick({ id: v.id, kind: 'video', url, thumbUrl: thumb, label: v.video_prompt || 'Video' }); onClose(); }}
                        className="aspect-video rounded-lg overflow-hidden border-2 border-white/10 hover:border-[#10B981] transition-all bg-black relative">
                        {thumb ? (
                          <img src={thumb} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-white/30">
                            <VideoIcon className="w-6 h-6" />
                          </div>
                        )}
                        <div className="absolute top-1 right-1 bg-black/70 text-white text-[9px] px-1.5 py-0.5 rounded">
                          VIDEO
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// HOLLYWOOD-PANEL (mit Slots + Director-Textbox + @-Mentions)
// ═══════════════════════════════════════════════════════════════

// Erzeugt einen kurzen, eindeutigen @-Namen pro Slot — wird im Director-Text
// als "@Image 1", "@Video 1", "@julia" usw. angezeigt.
function buildMentionName(item, slots) {
  if (item.kind === 'avatar') {
    const slug = (item.label || 'avatar').toString().trim().toLowerCase().replace(/[^a-z0-9_-]+/g, '');
    return slug || 'avatar';
  }
  // Image/Video durchnummerieren in Reihenfolge der Slots
  const sameKind = slots.filter(s => s.kind === item.kind).length;
  const prefix = item.kind === 'video' ? 'Video' : 'Image';
  return `${prefix} ${sameKind + 1}`;
}

const HollywoodSection = ({ open: openProp, onToggle, activeAvatarId, presetPrompt = null, presetSlots = null, onPresetConsumed = null, embedded = false }) => {
  // controlled mode: parent gibt open + onToggle. uncontrolled: lokaler State (Fallback).
  const [openLocal, setOpenLocal] = useState(false);
  const isControlled = typeof openProp === 'boolean';
  const open = isControlled ? openProp : openLocal;
  const handleToggle = () => (isControlled ? onToggle?.() : setOpenLocal(o => !o));

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTargetIdx, setPickerTargetIdx] = useState(null); // null = neuer Slot
  const [slots, setSlots] = useState([]); // Array von { slotKey, kind, url, thumbUrl, label, useAs, mentionName }
  const [directorText, setDirectorText] = useState('');
  const [generating, setGenerating] = useState(false);

  // Hollywood-Settings (4 Boxen unter dem Director-Text)
  const [soundOn, setSoundOn] = useState(true);
  const [duration, setDuration] = useState(7);            // Sekunden 1–15
  const [showDurationSlider, setShowDurationSlider] = useState(false);
  const [aspect, setAspect] = useState('auto');           // auto / 16:9 / 9:16 / 4:3 / 3:4 / 1:1 / 21:9
  const [resolution, setResolution] = useState('1080p');  // 480p / 720p / 1080p
  const [priceSnaps, setPriceSnaps] = useState(null);     // dynamischer SNAP-Preis für den Button

  // Preis live von der API (WaveSpeed-USD × 2 → SNAPs); reagiert auf Dauer/Auflösung/Referenzen
  useEffect(() => {
    let cancelled = false;
    const hasRefVid = slots.some(s => s.kind === 'video');
    (async () => {
      try {
        const r = await apiClient.get(`/api/hollywood/price?duration=${duration}&resolution=${encodeURIComponent(resolution)}&has_reference_video=${hasRefVid}`);
        if (!cancelled && r.ok) { const d = await r.json(); setPriceSnaps(d.snaps); }
      } catch (_) { /* Preis ist optional */ }
    })();
    return () => { cancelled = true; };
  }, [duration, resolution, slots]);

  // @-Mention State
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionPos, setMentionPos] = useState(0); // Position des @-Symbols im Text
  const [mentionActiveIdx, setMentionActiveIdx] = useState(0); // Tastatur-Navigation im Dropdown
  const [hoverPreview, setHoverPreview] = useState(null); // {url,left,top} große Chip-Vorschau (fixed, overflow-frei)
  const editorRef = useRef(null);
  const mentionCtxRef = useRef(null); // {node, atOffset, caretOffset} des @-Query-Bereichs

  const handlePick = (item) => {
    // Avatare sind IMMER Referenz (nicht als Start/End sinnvoll)
    const useAs = item.kind === 'avatar' ? 'reference' : 'reference';
    const mentionName = buildMentionName(item, slots);
    const newSlot = { ...item, slotKey: `slot-${Date.now()}-${Math.random().toString(36).slice(2,7)}`, useAs, mentionName };
    if (pickerTargetIdx === null) {
      setSlots(prev => [...prev, newSlot]);
    } else {
      setSlots(prev => prev.map((s, i) => i === pickerTargetIdx ? newSlot : s));
    }
    setPickerTargetIdx(null);
  };

  const removeSlot = (idx) => {
    setSlots(prev => prev.filter((_, i) => i !== idx));
  };

  const updateSlotUseAs = (idx, value) => {
    setSlots(prev => prev.map((s, i) => i === idx ? { ...s, useAs: value } : s));
  };

  // ── @-Mention Logik (contentEditable-Editor mit Inline-Chips) ────────────────
  const closeMention = () => { setMentionOpen(false); setMentionFilter(''); mentionCtxRef.current = null; };

  // Editor-DOM → reiner Text mit @mentions (für Backend-Payload)
  const serializeEditor = () => {
    const root = editorRef.current;
    if (!root) return '';
    let out = '';
    const walk = (node) => {
      node.childNodes.forEach((n) => {
        if (n.nodeType === Node.TEXT_NODE) {
          out += n.textContent.replace(/\u00A0/g, ' ');
        } else if (n.nodeType === Node.ELEMENT_NODE) {
          if (n.dataset && n.dataset.mention) {
            out += '@' + n.dataset.mention;
          } else if (n.tagName === 'BR') {
            out += '\n';
          } else if (n.tagName === 'DIV') {
            if (out && !out.endsWith('\n')) out += '\n';
            walk(n);
          } else {
            walk(n);
          }
        }
      });
    };
    walk(root);
    return out.replace(/\n$/, '');
  };

  // @-Query am Cursor erkennen → Dropdown öffnen
  const detectMention = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) { closeMention(); return; }
    const range = sel.getRangeAt(0);
    const node = range.startContainer;
    if (node.nodeType !== Node.TEXT_NODE) { closeMention(); return; }
    const upto = node.textContent.slice(0, range.startOffset);
    const at = upto.lastIndexOf('@');
    if (at < 0) { closeMention(); return; }
    const q = upto.slice(at + 1);
    if (/\s/.test(q)) { closeMention(); return; }
    mentionCtxRef.current = { node, atOffset: at, caretOffset: range.startOffset };
    setMentionFilter(q.toLowerCase());
    setMentionActiveIdx(0);
    setMentionOpen(true);
  };

  const handleEditorInput = () => {
    setDirectorText(serializeEditor());
    detectMention();
  };

  // Klick auf das X eines Chips → Chip entfernen
  const handleEditorClick = (e) => {
    const x = e.target && e.target.closest ? e.target.closest('.hw-chip-x') : null;
    if (x) {
      e.preventDefault();
      const chip = x.closest('.hw-chip');
      if (chip) { chip.remove(); setDirectorText(serializeEditor()); }
    }
  };

  // Große Chip-Vorschau als FIXED-Element (entkommt dem Editor-Overflow)
  const handleEditorMouseOver = (e) => {
    const chip = e.target && e.target.closest ? e.target.closest('.hw-chip') : null;
    if (chip && chip.dataset.preview) {
      const r = chip.getBoundingClientRect();
      const PH = 240, GAP = 8;
      const above = r.top >= PH + GAP + 8; // genug Platz oberhalb? sonst drunter
      const left = Math.max(8, Math.min(r.left, window.innerWidth - PH - 16));
      setHoverPreview({ url: chip.dataset.preview, left, above, anchorTop: r.top, anchorBottom: r.bottom });
    }
  };
  const handleEditorMouseOut = (e) => {
    const chip = e.target && e.target.closest ? e.target.closest('.hw-chip') : null;
    const to = e.relatedTarget;
    if (chip && (!to || !chip.contains(to))) setHoverPreview(null);
  };

  // "Prompt übernehmen": Text + Referenz-Slots wiederherstellen, sobald der Bereich offen ist
  useEffect(() => {
    const hasPrompt = presetPrompt != null && presetPrompt !== '';
    const hasSlots = Array.isArray(presetSlots) && presetSlots.length > 0;
    if ((!hasPrompt && !hasSlots) || !open) return;
    const root = editorRef.current;
    if (hasPrompt && root) {
      root.textContent = presetPrompt;
      setDirectorText(presetPrompt);
      try {
        root.focus();
        const sel = window.getSelection();
        const r = document.createRange();
        r.selectNodeContents(root); r.collapse(false);
        sel.removeAllRanges(); sel.addRange(r);
      } catch (_) {}
    }
    if (hasSlots) {
      setSlots(presetSlots.map((s, i) => ({
        ...s,
        slotKey: `slot-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 6)}`,
        thumbUrl: s.thumbUrl || s.url,
        useAs: s.useAs || (s.kind === 'avatar' ? 'reference' : 'reference'),
        mentionName: s.mentionName || s.label || 'ref',
      })));
    }
    if (onPresetConsumed) onPresetConsumed();
    // eslint-disable-next-line
  }, [presetPrompt, presetSlots, open]);

  // Referenz als Inline-Chip einfügen (ersetzt den @query, Cursor sauber dahinter)
  const insertMentionChip = (slot) => {
    const root = editorRef.current;
    if (!root) return;
    root.focus();
    const ctx = mentionCtxRef.current;
    let range = document.createRange();
    if (ctx && ctx.node && ctx.node.parentNode) {
      const len = ctx.node.textContent.length;
      range.setStart(ctx.node, Math.min(ctx.atOffset, len));
      range.setEnd(ctx.node, Math.min(ctx.caretOffset, len));
      range.deleteContents();
    } else {
      const sel = window.getSelection();
      if (sel && sel.rangeCount) range = sel.getRangeAt(0);
    }
    const chip = document.createElement('span');
    chip.className = 'hw-chip';
    chip.setAttribute('contenteditable', 'false');
    chip.dataset.mention = slot.mentionName;
    chip.dataset.preview = slot.url || slot.thumbUrl || '';
    const thumb = slot.thumbUrl ? `<img class="hw-chip-thumb" src="${slot.thumbUrl}" alt="" />` : '';
    chip.innerHTML = `${thumb}<span class="hw-chip-name">@${slot.mentionName}</span><span class="hw-chip-x" data-x="1">\u00D7</span>`;
    range.insertNode(chip);
    // Leerzeichen + Cursor sauber HINTER den Chip
    const space = document.createTextNode('\u00A0');
    chip.parentNode.insertBefore(space, chip.nextSibling);
    const sel = window.getSelection();
    const after = document.createRange();
    after.setStart(space, 1);
    after.collapse(true);
    sel.removeAllRanges();
    sel.addRange(after);
    closeMention();
    setDirectorText(serializeEditor());
  };

  const filteredMentions = slots.filter(s => {
    if (!mentionFilter) return true;
    return s.mentionName.toLowerCase().includes(mentionFilter)
        || (s.label || '').toLowerCase().includes(mentionFilter);
  });

  // Tastatur: hoch/runter durch die Referenz-Liste, Enter bestätigt, Esc schließt
  const handleDirectorKeyDown = (e) => {
    if (!mentionOpen || filteredMentions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMentionActiveIdx(i => (i + 1) % filteredMentions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMentionActiveIdx(i => (i - 1 + filteredMentions.length) % filteredMentions.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      insertMentionChip(filteredMentions[Math.min(mentionActiveIdx, filteredMentions.length - 1)]);
    } else if (e.key === 'Escape') {
      setMentionOpen(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      // avatar_id MITSENDEN — sonst fällt das Backend auf den Datei-Pfad zurück und
      // der Eintrag taucht in keiner Avatar-gefilterten Videogalerie auf (kein Spinner).
      const effAvatarId = activeAvatarId || JSON.parse(localStorage.getItem('snapshot_current_avatar_id') || 'null');
      const payload = {
        avatar_id: effAvatarId,
        director: directorText,
        slots: slots.map(s => ({
          kind: s.kind,
          url: s.url,
          useAs: s.useAs,
          label: s.label || null,
          mentionName: s.mentionName || null,
        })),
        settings: { sound: soundOn, duration_s: duration, aspect, resolution },
      };
      const res = await fetch('/api/hollywood/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('snapshot_token')}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        let msg = 'Bitte versuche es erneut.';
        try { const j = await res.json(); msg = j.detail || msg; } catch (_) {}
        throw new Error(msg);
      }
      // Backend hat den DB-Job angelegt + Pipeline gestartet — Galerie übernimmt von hier
      // Felder leeren, ready für nächste Generation
      slots.forEach(s => { if (s.previewUrl) URL.revokeObjectURL(s.previewUrl); });
      setSlots([]);
      setDirectorText('');
      if (editorRef.current) editorRef.current.innerHTML = '';
      // Galerie öffnen + dahin scrollen
      try {
        window.dispatchEvent(new CustomEvent('snapshot:setGalleryTab', { detail: { tab: 'videos' } }));
        window.dispatchEvent(new CustomEvent('refreshVideoGallery'));
      } catch (_) {}
      setTimeout(() => {
        const target = document.querySelector('[data-testid="gallery-section"]');
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    } catch (err) {
      console.error('Hollywood-Generate failed:', err);
      alert(err.message || 'Bitte versuche es erneut.');
    } finally {
      setGenerating(false);
    }
  };

  const canAddMore = slots.length < MAX_REFERENCES;

  return (
    <div
      data-accordion="hollywood"
      className={embedded ? "" : "border-t-4 border-[#10B981]/40 rounded-xl overflow-hidden bg-gradient-to-br from-amber-500/5 to-transparent backdrop-blur-sm"}
      style={embedded ? undefined : { scrollMarginTop: '120px' }}
    >
      {/* Accordion-Header — nur wenn nicht embedded */}
      {!embedded && (
      <button
        onClick={handleToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-all"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-amber-500/30 to-amber-700/20 rounded-xl flex items-center justify-center">
            <Film className="w-5 h-5 text-amber-400" />
          </div>
          <div className="text-left">
            <h3 className="text-white font-bold text-lg flex items-center gap-2">
              Hollywood
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">
                NEU
              </span>
            </h3>
            <p className="text-amber-400/70 text-xs">
              Referenzen kombinieren · Multi-Source · Storytelling
            </p>
          </div>
        </div>
        {open ? <ChevronUp className="w-6 h-6 text-amber-400" /> : <ChevronDown className="w-6 h-6 text-white/50" />}
      </button>
      )}

      {(open || embedded) && (
        <div className="relative border-t border-amber-500/20 animate-slide-down overflow-hidden">
          {/* Fester Modus-Hintergrund (Hollywood) */}
          <div className="absolute top-0 left-0 right-0 overflow-hidden" style={{ zIndex: 0 }}>
            <img src="/mode-cards/bg-mode-hollywood.png" alt="" aria-hidden className="w-full h-auto block object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.5) 120px, rgba(0,0,0,0.78) 220px, rgba(0,0,0,0.92) 320px, black 420px)' }} />
          </div>
          <div className="relative p-4 sm:p-6 space-y-5" style={{ zIndex: 1 }}>
          {/* Slots-Reihe */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-amber-400/80 mb-2">
              Referenzen <span className="text-white/40 normal-case font-normal">({slots.length} / {MAX_REFERENCES})</span>
            </label>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
              {slots.map((slot, idx) => (
                <div key={slot.slotKey} className="flex-shrink-0 w-32 sm:w-36">
                  {/* Vorschau */}
                  <div className="relative aspect-[3/4] rounded-xl border-2 border-[#10B981]/40 overflow-hidden bg-black">
                    {slot.kind === 'video' ? (
                      <>
                        <img src={slot.thumbUrl} alt="" className="w-full h-full object-cover" />
                        <div className="absolute top-1 right-1 bg-black/70 text-white text-[9px] px-1.5 py-0.5 rounded font-bold">
                          VIDEO
                        </div>
                      </>
                    ) : (
                      <img src={slot.thumbUrl || slot.url} alt="" className="w-full h-full object-cover" />
                    )}
                    {slot.kind === 'avatar' && (
                      <div className="absolute top-1 left-1 bg-[#10B981]/90 text-black text-[9px] px-1.5 py-0.5 rounded font-bold">
                        AVATAR
                      </div>
                    )}
                    <button
                      onClick={() => removeSlot(idx)}
                      className="absolute top-1 right-1 bg-black/80 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-500 transition-all"
                      title="Entfernen"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                  {/* Use-As Dropdown — Avatare sind IMMER Referenz, kein Dropdown */}
                  {slot.kind === 'avatar' ? (
                    <div className="mt-2 w-full px-2 py-1.5 text-xs bg-[#10B981]/10 border border-[#10B981]/30 rounded-lg text-[#10B981] text-center font-semibold">
                      Referenz (Avatar)
                    </div>
                  ) : (
                    <select
                      value={slot.useAs}
                      onChange={(e) => updateSlotUseAs(idx, e.target.value)}
                      className="mt-2 w-full px-2 py-1.5 text-xs bg-white/5 border border-white/15 rounded-lg text-white focus:border-[#10B981] focus:outline-none"
                    >
                      {USE_AS_OPTIONS.map(o => (
                        <option key={o.value} value={o.value} className="bg-black">{o.label}</option>
                      ))}
                    </select>
                  )}
                  {/* Mention-Name als kleiner Hinweis */}
                  <div className="mt-1 text-[10px] text-white/40 text-center font-mono truncate">@{slot.mentionName}</div>
                </div>
              ))}
              {/* Add-Slot */}
              {canAddMore && (
                <button
                  onClick={() => { setPickerTargetIdx(null); setPickerOpen(true); }}
                  className="flex-shrink-0 w-32 sm:w-36 aspect-[3/4] rounded-xl border-2 border-dashed border-white/30 bg-white/5 hover:border-[#10B981] hover:bg-[#10B981]/5 transition-all flex flex-col items-center justify-center gap-2 text-white/50 hover:text-[#10B981]"
                >
                  <Plus className="w-7 h-7" />
                  <span className="text-xs font-medium">
                    {slots.length === 0 ? 'Referenz wählen' : 'weitere'}
                  </span>
                </button>
              )}
            </div>
            {!canAddMore && (
              <p className="text-amber-400/70 text-xs mt-2 flex items-center gap-1.5">
                <Info className="w-3.5 h-3.5" />
                Maximum von {MAX_REFERENCES} Referenzen erreicht
              </p>
            )}
          </div>

          {/* Director-Textbox mit @-Mention-Dropdown */}
          <div className="relative">
            <label className="block text-xs font-bold uppercase tracking-wider text-amber-400/80 mb-2">
              Du bist der Regisseur
            </label>
            <style>{`
              .hw-editor:empty:before { content: attr(data-placeholder); color: rgba(255,255,255,0.30); pointer-events: none; white-space: pre-wrap; }
              .hw-chip { display:inline-flex; align-items:center; gap:4px; background:rgba(16,185,129,0.15); border:1px solid rgba(16,185,129,0.5); border-radius:8px; padding:1px 5px 1px 2px; margin:0 1px; position:relative; vertical-align:middle; user-select:none; white-space:nowrap; }
              .hw-chip-thumb { width:18px; height:18px; border-radius:4px; object-fit:cover; flex:none; }
              .hw-chip-name { color:#10B981; font-weight:600; font-size:0.85em; }
              .hw-chip-x { display:none; cursor:pointer; color:#fff; background:rgba(0,0,0,0.65); border-radius:9999px; width:15px; height:15px; line-height:15px; text-align:center; font-size:11px; margin-left:2px; }
              .hw-chip:hover .hw-chip-x { display:inline-block; }
            `}</style>
            <div
              ref={editorRef}
              contentEditable
              suppressContentEditableWarning
              onInput={handleEditorInput}
              onKeyDown={handleDirectorKeyDown}
              onClick={handleEditorClick}
              onMouseOver={handleEditorMouseOver}
              onMouseOut={handleEditorMouseOut}
              onBlur={() => setTimeout(() => setMentionOpen(false), 150)}
              data-placeholder={"Erzähl uns, was in deiner Geschichte passieren soll. Oben kannst du Start-/End-Frame oder Referenzen wählen.\n\nEine Referenz fügst du mit dem @-Symbol ein."}
              className="hw-editor w-full min-h-[150px] px-4 py-3 rounded-xl bg-white/5 border-2 border-white/15 text-white focus:border-[#10B981] focus:outline-none transition-all text-sm leading-relaxed whitespace-pre-wrap break-words overflow-y-auto"
            />
            <p className="mt-1.5 text-[11px] text-white/40 flex items-center gap-1.5">
              <span className="font-mono bg-white/10 px-1.5 py-0.5 rounded text-[10px] text-white/60">@</span>
              Tippe das @-Symbol, um eine deiner Referenzen einzufügen.
            </p>

            {/* @-Mention-Dropdown — schwebt über dem Textfeld */}
            {mentionOpen && filteredMentions.length > 0 && (
              <div className="absolute left-0 right-0 z-30 mt-1 bg-black border-2 border-[#10B981]/40 rounded-xl shadow-2xl shadow-[#10B981]/20 overflow-hidden max-h-64 overflow-y-auto"
                   style={{ top: 'calc(2rem + 0.5rem)' }}>
                {filteredMentions.map((slot, idx) => (
                  <button
                    key={slot.slotKey}
                    onMouseEnter={() => setMentionActiveIdx(idx)}
                    onMouseDown={(e) => { e.preventDefault(); insertMentionChip(slot); }}
                    className={`w-full flex items-center gap-3 px-3 py-2 transition-colors text-left ${idx === mentionActiveIdx ? 'bg-[#10B981]/20' : 'hover:bg-white/5'}`}
                  >
                    <div className="w-9 h-9 rounded-md overflow-hidden flex-shrink-0 bg-white/5 border border-white/10">
                      {slot.thumbUrl ? (
                        <img src={slot.thumbUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full" />
                      )}
                    </div>
                    <div className="text-white text-sm font-semibold truncate">Add @{slot.mentionName}</div>
                  </button>
                ))}
              </div>
            )}
            {/* Kleiner Hinweis wenn @ getippt aber keine Refs da sind */}
            {mentionOpen && filteredMentions.length === 0 && (
              <div className="absolute left-0 right-0 z-30 mt-1 bg-black border-2 border-white/20 rounded-xl px-4 py-3"
                   style={{ top: 'calc(2rem + 0.5rem)' }}>
                <p className="text-white/50 text-xs">
                  {slots.length === 0
                    ? 'Noch keine Referenzen — füge oben mit dem + erst eine hinzu.'
                    : `Keine Referenz mit „${mentionFilter}" gefunden.`}
                </p>
              </div>
            )}
            {hoverPreview && (
              <img
                src={hoverPreview.url}
                alt=""
                style={{ position: 'fixed', left: hoverPreview.left, maxWidth: 240, maxHeight: 240, width: 'auto', height: 'auto', borderRadius: 10, border: '2px solid rgba(16,185,129,0.7)', boxShadow: '0 12px 34px rgba(0,0,0,0.65)', zIndex: 9999, pointerEvents: 'none', ...(hoverPreview.above ? { top: hoverPreview.anchorTop - 8, transform: 'translateY(-100%)' } : { top: hoverPreview.anchorBottom + 8 }) }}
              />
            )}
          </div>

          {/* Settings: Sound · Zeit · Format · Auflösung */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-bold uppercase tracking-wider text-amber-400/80">
                Einstellungen
              </label>
              <span className="text-[11px] text-white/50 flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-[#10B981]" />
                Beste Ergebnisse mit <span className="text-[#10B981] font-semibold">1080p</span>
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">

              {/* SOUND */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-3">
                <div className="text-[10px] font-bold uppercase tracking-wider text-white/60 mb-2 text-center flex items-center justify-center gap-1">
                  {soundOn ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}
                  Sound
                </div>
                <div className="grid grid-cols-2 gap-1">
                  {[
                    { v: true,  label: 'An'  },
                    { v: false, label: 'Aus' },
                  ].map(o => (
                    <button key={String(o.v)} onClick={() => setSoundOn(o.v)}
                      className={`h-9 rounded-lg text-xs font-semibold transition-all ${
                        soundOn === o.v
                          ? 'bg-[#10B981] text-black shadow-lg shadow-[#10B981]/20'
                          : 'bg-white/5 text-white/70 border border-white/10 hover:bg-white/10'
                      }`}>{o.label}</button>
                  ))}
                </div>
              </div>

              {/* ZEIT (Slider-Klappbox) */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-3 relative">
                <div className="text-[10px] font-bold uppercase tracking-wider text-white/60 mb-2 text-center flex items-center justify-center gap-1">
                  <Clock className="w-3 h-3" />
                  Zeit
                </div>
                <button
                  onClick={() => setShowDurationSlider(s => !s)}
                  className="w-full h-9 rounded-lg bg-white/5 border border-white/10 text-white text-sm font-bold hover:bg-white/10 transition-all"
                >
                  {duration} s
                </button>
                {showDurationSlider && (
                  <div className="absolute left-2 right-2 top-full mt-2 z-20 bg-black border-2 border-[#10B981]/40 rounded-xl p-3 shadow-2xl shadow-[#10B981]/20">
                    <div className="flex items-center gap-2 text-xs text-white/70 mb-1">
                      <span>1s</span>
                      <input
                        type="range" min={1} max={15} step={1}
                        value={duration}
                        onChange={(e) => setDuration(parseInt(e.target.value, 10))}
                        className="flex-1 accent-[#10B981]"
                      />
                      <span>15s</span>
                    </div>
                    <div className="text-center text-[#10B981] font-bold text-sm">{duration} Sekunden</div>
                    <button
                      onClick={() => setShowDurationSlider(false)}
                      className="mt-2 w-full text-[11px] py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-white/70"
                    >
                      Übernehmen
                    </button>
                  </div>
                )}
              </div>

              {/* FORMAT */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-3">
                <div className="text-[10px] font-bold uppercase tracking-wider text-white/60 mb-2 text-center flex items-center justify-center gap-1">
                  <Maximize2 className="w-3 h-3" />
                  Format
                </div>
                <select
                  value={aspect}
                  onChange={(e) => setAspect(e.target.value)}
                  className="w-full h-9 px-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm font-bold text-center hover:bg-white/10 focus:border-[#10B981] focus:outline-none transition-all"
                >
                  <option value="auto"  className="bg-black">Auto</option>
                  <option value="16:9"  className="bg-black">16:9</option>
                  <option value="9:16"  className="bg-black">9:16</option>
                  <option value="4:3"   className="bg-black">4:3</option>
                  <option value="3:4"   className="bg-black">3:4</option>
                  <option value="1:1"   className="bg-black">1:1</option>
                  <option value="21:9"  className="bg-black">21:9</option>
                </select>
              </div>

              {/* AUFLÖSUNG */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-3">
                <div className="text-[10px] font-bold uppercase tracking-wider text-white/60 mb-2 text-center flex items-center justify-center gap-1">
                  <Sparkles className="w-3 h-3 text-[#10B981]" />
                  Auflösung
                </div>
                <select
                  value={resolution}
                  onChange={(e) => setResolution(e.target.value)}
                  className="w-full h-9 px-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm font-bold text-center hover:bg-white/10 focus:border-[#10B981] focus:outline-none transition-all"
                >
                  <option value="480p"  className="bg-black">480p</option>
                  <option value="720p"  className="bg-black">720p</option>
                  <option value="1080p" className="bg-black">1080p ✨</option>
                </select>
              </div>

            </div>
          </div>

          {/* Generate-Button */}
          <button
            onClick={handleGenerate}
            disabled={generating || (slots.length === 0 && !directorText.trim())}
            className={`w-full px-5 py-3 rounded-xl font-bold transition-all flex flex-col items-center justify-center gap-0.5 ${
              !generating && (slots.length > 0 || directorText.trim())
                ? 'bg-gradient-to-br from-amber-500 to-amber-700 text-black shadow-lg hover:shadow-amber-500/30'
                : 'bg-white/10 text-white/40 cursor-not-allowed border-2 border-white/15'
            }`}
          >
            {generating ? 'Wird vorbereitet …' : (
              <>
                <span className="flex items-center gap-2">
                  <Film className="w-5 h-5" />
                  Hollywood-Video erstellen
                </span>
                {priceSnaps != null && (
                  <span className="text-xs font-semibold opacity-90">{priceSnaps} SNAPs</span>
                )}
              </>
            )}
          </button>

          </div>
        </div>
      )}

      {/* Picker-Modal */}
      <ReferencePickerModal
        open={pickerOpen}
        activeAvatarId={activeAvatarId}
        onClose={() => { setPickerOpen(false); setPickerTargetIdx(null); }}
        onPick={(item) => { handlePick(item); setPickerOpen(false); }}
      />
    </div>
  );
};

export default HollywoodSection;
